/**
 * SyncSettingsPanel - Auto-sync configuration panel
 * Extracted from DevPage.tsx for modularity
 */

import { useState } from 'react';
import { Card, CardHeader } from '@/components/shared';
import { syncManager, SyncSettings } from '@services/syncManager';
import { useSyncStatus } from '@/hooks/useSyncStatus';

export function SyncSettingsPanel() {
  const [settings, setSettings] = useState<SyncSettings>(syncManager.getSettings());
  const syncStatus = useSyncStatus();

  const handleChange = <K extends keyof SyncSettings>(key: K, value: SyncSettings[K]) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    syncManager.updateSettings({ [key]: value });
  };

  return (
    <Card>
      <CardHeader
        title="Auto-Sync Settings"
        subtitle="Configure automatic synchronization (owner only)"
      />

      {/* Current Status */}
      <div className="sync-status-display">
        <div className="status-row">
          <span>Auto-sync:</span>
          <span className={syncStatus.autoSyncEnabled ? 'status-on' : 'status-off'}>
            {syncStatus.autoSyncEnabled ? 'ON' : 'OFF'}
          </span>
        </div>
        <div className="status-row">
          <span>Connection:</span>
          <span className={`status-${syncStatus.connectionStatus}`}>
            {syncStatus.connectionStatus}
          </span>
        </div>
        <div className="status-row">
          <span>Pending changes:</span>
          <span>{syncStatus.pendingChanges}</span>
        </div>
        {syncStatus.lastError && (
          <div className="status-row error">
            <span>Last error:</span>
            <span>{syncStatus.lastError}</span>
          </div>
        )}
      </div>

      {/* Settings Controls */}
      <div className="sync-settings-controls">
        <label className="setting-checkbox">
          <input
            type="checkbox"
            checked={settings.autoSyncEnabled}
            onChange={(e) => handleChange('autoSyncEnabled', e.target.checked)}
          />
          <span>Auto-Sync Enabled</span>
        </label>

        <label className="setting-checkbox">
          <input
            type="checkbox"
            checked={settings.syncOnLogin}
            onChange={(e) => handleChange('syncOnLogin', e.target.checked)}
            disabled={!settings.autoSyncEnabled}
          />
          <span>Sync on Login</span>
        </label>

        <label className="setting-checkbox">
          <input
            type="checkbox"
            checked={settings.syncOnFocus}
            onChange={(e) => handleChange('syncOnFocus', e.target.checked)}
            disabled={!settings.autoSyncEnabled}
          />
          <span>Sync when Tab Gains Focus</span>
        </label>

        <label className="setting-checkbox">
          <input
            type="checkbox"
            checked={settings.syncOnMutation}
            onChange={(e) => handleChange('syncOnMutation', e.target.checked)}
            disabled={!settings.autoSyncEnabled}
          />
          <span>Sync after Data Changes</span>
        </label>

        <div className="setting-row">
          <label>Push Debounce (seconds):</label>
          <input
            type="number"
            min="5"
            max="300"
            value={settings.pushDebounceMs / 1000}
            onChange={(e) => handleChange('pushDebounceMs', Number(e.target.value) * 1000)}
            disabled={!settings.autoSyncEnabled}
            className="setting-number"
          />
        </div>

        <div className="setting-row">
          <label>Background Pull Interval (0 = off):</label>
          <input
            type="number"
            min="0"
            max="3600"
            value={settings.pullIntervalMs / 1000}
            onChange={(e) => handleChange('pullIntervalMs', Number(e.target.value) * 1000)}
            disabled={!settings.autoSyncEnabled}
            className="setting-number"
          />
          <span className="setting-hint">seconds (0 = disabled)</span>
        </div>

        <div className="setting-row">
          <label>Conflict Strategy:</label>
          <select
            value={settings.conflictStrategy}
            onChange={(e) => handleChange('conflictStrategy', e.target.value as SyncSettings['conflictStrategy'])}
            disabled={!settings.autoSyncEnabled}
            className="setting-select"
          >
            <option value="last-write-wins">Last Write Wins</option>
            <option value="server-wins">Server Wins</option>
            <option value="client-wins">Client Wins</option>
          </select>
        </div>
      </div>

      {/* Debug Info */}
      <details className="sync-debug-details">
        <summary>Debug Info</summary>
        <pre className="debug-json">
          {JSON.stringify(syncManager.getDebugInfo(), null, 2)}
        </pre>
      </details>
    </Card>
  );
}
